"use client";

import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Loader2, Info } from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";

import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { useGetPremiumByCarrier } from "../../hooks/use-hook";

// Colors for the pie chart slices - extended palette for many carriers
const COLORS = [
  "#0088FE", // Blue
  "#00C49F", // Teal
  "#FFBB28", // Yellow
  "#FF8042", // Orange
  "#8884D8", // Purple
  "#82CA9D", // Green
  "#FFC658", // Gold
  "#FF6B6B", // Red
  "#4ECDC4", // Cyan
  "#45B7D1", // Light Blue
  "#96CEB4", // Sage
  "#E17055", // Coral
  "#6C5CE7", // Indigo
  "#00B894", // Mint
  "#FDCB6E", // Sunflower
  "#E84393", // Pink
  "#74B9FF", // Sky Blue
  "#A29BFE", // Lavender
  "#55A3FF", // Bright Blue
  "#FF9F43", // Tangerine
];

interface CarrierBreakdownProps {
  agencyId?: string; // Optional - if not provided, uses current agency from token manager
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
  inTab?: boolean; // When true, renders without Card wrapper
  showChartOnly?: boolean; // When true, only show the pie chart
  showTableOnly?: boolean; // When true, only show the data table
}

export const CarrierBreakdown: React.FC<CarrierBreakdownProps> = ({
  agencyId: propAgencyId,
  period = '6-months',
  startDate,
  endDate,
  inTab = false,
  showChartOnly = false,
  showTableOnly = false,
}) => {
  const agencyId = propAgencyId || AgencyTokenManager.getCurrentAgencyId() || '';

  // Shell: the header ⓘ tooltips render un-portalled INSIDE the horizontal
  // scroller, so the overflow clip swallows them. Tap-to-toggle a fixed panel
  // instead (same pattern as the leaderboard header). Desktop keeps Radix.
  const isShell =
    typeof window !== "undefined" && window.location.pathname.startsWith("/m");
  const [helpTip, setHelpTip] = React.useState<{
    top: number;
    left: number;
    text: string;
  } | null>(null);
  const showHelp = (e: React.MouseEvent, text: string) => {
    e.stopPropagation();
    if (helpTip) {
      setHelpTip(null);
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 240;
    const left = Math.min(
      Math.max(8, r.left + r.width / 2 - width / 2),
      window.innerWidth - width - 8,
    );
    setHelpTip({ top: r.bottom + 8, left, text });
  };
  const HeaderInfo = ({ text }: { text: string }) =>
    isShell ? (
      <button
        type="button"
        aria-label="Info"
        onClick={(e) => showHelp(e, text)}
        className="inline-flex items-center align-middle"
      >
        <Info className="h-3.5 w-3.5 text-primary/70" />
      </button>
    ) : (
      <TooltipProvider>
        <UITooltip>
          <TooltipTrigger asChild>
            <button type="button" aria-label="Info" className="inline-flex cursor-help items-center align-middle">
              <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64 text-left text-xs font-normal leading-snug text-popover-foreground">
            {text}
          </TooltipContent>
        </UITooltip>
      </TooltipProvider>
    );

  const { data: carrierData, isLoading, error } = useGetPremiumByCarrier(
    agencyId,
    period,
    startDate,
    endDate
  );

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Custom tooltip
  // Issued total across carriers — shown beside the card title so the pie's
  // gross slicing can't be mistaken for issued business.
  // carrierData is undefined until the query resolves (see the !carrierData
  // guard further down) — default to [] or this throws on first render.
  const issuedTotal = ((carrierData ?? []) as Array<{ issuedPremium?: number }>).reduce(
    (sum, r) => sum + Number(r.issuedPremium || 0),
    0,
  );

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold text-sm">{data.carrier}</p>
          <p className="text-sm text-gray-600">
            Total production: {formatCurrency(data.totalPremium)}
          </p>
          <p className="text-sm text-emerald-600">
            Total premium sold: {formatCurrency(data.issuedPremium || 0)}
          </p>
          <p className="text-sm text-gray-600">
            Policies: {data.policyCount}
          </p>
          <p className="text-sm text-primary font-medium">
            {data.percentage}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label for pie slices
  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: any) => {
    // Only show label if percentage is > 5%
    if (percent < 0.05) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-xs font-medium"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (!agencyId) {
    return null;
  }

  const content = (
    <>
      {/* Shell help panel — fixed, so the table's overflow clip can't eat it */}
      {helpTip && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setHelpTip(null)} />
          <div
            className="fixed z-[9999] w-[240px] rounded-lg bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white shadow-xl"
            style={{ top: helpTip.top, left: helpTip.left }}
          >
            {helpTip.text}
          </div>
        </>
      )}
      {isLoading ? (
        <div className="flex items-center justify-center h-[300px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-[300px] text-gray-500">
          Failed to load carrier data
        </div>
      ) : !carrierData || carrierData.length === 0 ? (
        <div className="flex items-center justify-center h-[300px] text-gray-500">
          No carrier data available for this period
        </div>
      ) : (
        <>
          {/* Show only pie chart if showChartOnly */}
          {showChartOnly && (
            <div className="overflow-hidden">
              <ResponsiveContainer width={380} height={380}>
                <PieChart>
                  <Pie
                    data={carrierData as any[]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={150}
                    fill="#8884d8"
                    dataKey="totalPremium"
                    nameKey="carrier"
                  >
                    {carrierData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Carrier names legend below chart - constrained width to wrap */}
              <div className="mt-3 max-w-[380px] max-h-[120px] overflow-y-auto">
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {carrierData.map((row, index) => (
                    <div key={row.carrier} className="flex items-center gap-1 truncate">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-gray-700 truncate">{row.carrier}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Show only table if showTableOnly */}
          {showTableOnly && (
            /* Fill the wrapper rather than capping at a fixed height — the
               parent gives this column 500px, and a 300px cap left 200px of
               dead space while hiding carriers behind a scrollbar. */
            <div className="carrier-table-scroll h-full overflow-y-auto" data-no-ptr>
              <table className="carrier-table w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Carrier</th>
                    <th className="text-right p-3 font-medium text-gray-600">
                      <span className="inline-flex items-center justify-end gap-1 whitespace-nowrap">
                        Total Production
                        <HeaderInfo text="Everything marked sold and approved as well as in underwriting." />
                      </span>
                    </th>
                    <th className="text-right p-3 font-medium text-gray-600">
                      <span className="inline-flex items-center justify-end gap-1 whitespace-nowrap">
                        Total Premium Sold
                        <HeaderInfo text="Only policies marked sold and approved." />
                      </span>
                    </th>
                    <th className="text-right p-3 font-medium text-gray-600">Policies</th>
                    <th className="text-right p-3 font-medium text-gray-600">%</th>
                  </tr>
                </thead>
                <tbody>
                  {carrierData.map((row, index) => (
                    <tr key={row.carrier} className="border-t hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span>{row.carrier}</span>
                        </div>
                      </td>
                      <td className="text-right p-3 font-medium whitespace-nowrap">
                        {formatCurrency(row.totalPremium)}
                      </td>
                      <td className="text-right p-3 font-medium whitespace-nowrap text-emerald-600">
                        {formatCurrency((row as { issuedPremium?: number }).issuedPremium || 0)}
                      </td>
                      <td className="text-right p-3">{row.policyCount}</td>
                      <td className="text-right p-3 text-primary">{row.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Show both (default) if neither showChartOnly nor showTableOnly */}
          {!showChartOnly && !showTableOnly && (
            <div className="flex flex-col gap-4">
              {/* Pie Chart centered */}
              <div className="flex justify-center">
                <ResponsiveContainer width={280} height={280}>
                  <PieChart>
                    <Pie
                      data={carrierData as any[]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="totalPremium"
                      nameKey="carrier"
                    >
                      {carrierData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Data Table - full width below chart. carrier-table-scroll +
                  data-no-ptr: on the shell the 5-column table swipes sideways
                  inside this wrapper (PullToRefresh must not eat the gesture). */}
              <div className="carrier-table-scroll w-full" data-no-ptr>
                <table className="carrier-table w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 font-medium text-gray-600">Carrier</th>
                      <th className="text-right p-2 font-medium text-gray-600">
                        <span className="inline-flex items-center justify-end gap-1 whitespace-nowrap">
                          Total Production
                          <HeaderInfo text="Everything marked sold and approved as well as in underwriting." />
                        </span>
                      </th>
                      <th className="text-right p-2 font-medium text-gray-600">
                        <span className="inline-flex items-center justify-end gap-1 whitespace-nowrap">
                          Total Premium Sold
                          <HeaderInfo text="Only policies marked sold and approved." />
                        </span>
                      </th>
                      <th className="text-right p-2 font-medium text-gray-600">Policies</th>
                      <th className="text-right p-2 font-medium text-gray-600">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carrierData.map((row, index) => (
                      <tr key={row.carrier} className="border-t hover:bg-gray-50">
                        <td className="p-2 flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span>{row.carrier}</span>
                        </td>
                        <td className="text-right p-2 font-medium whitespace-nowrap">
                          {formatCurrency(row.totalPremium)}
                        </td>
                        <td className="text-right p-2 font-medium whitespace-nowrap text-emerald-600">
                          {formatCurrency((row as { issuedPremium?: number }).issuedPremium || 0)}
                        </td>
                        <td className="text-right p-2">{row.policyCount}</td>
                        <td className="text-right p-2 text-primary">{row.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  // When in a tab, render without Card wrapper
  if (inTab) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Production by Carrier
          <span className="ml-2 text-sm font-medium text-emerald-600">
            {formatCurrency(issuedTotal)} premium sold
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default CarrierBreakdown;
